import React from "react"

import {
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Divider,
  inputVariants,
} from "@coval/ui"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "cmdk"
import { Search } from "lucide-react"

import type { AppCommand, ResolvedAppCommand } from "@/lib/appCommands"
import {
  COMMAND_PALETTE_OPEN_EVENT,
  type CommandPaletteOpenSource,
} from "@/lib/commandPaletteEvents"
import { logger } from "@/lib/logger"
import { useRegisterCommands } from "@/contexts/CommandRegistryContext"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { usePageShortcuts } from "@/hooks/usePageShortcuts"

import { useAvailableCommands } from "../hooks/useAvailableCommands"
import type { NavigationFeatureFlag } from "../types"

interface CommandSection {
  heading: string
  id: string
  matches: (command: ResolvedAppCommand) => boolean
}

const COMMAND_SECTIONS: CommandSection[] = [
  {
    heading: "Focused Item",
    id: "focused-item",
    matches: (command) => command.scope === "selection",
  },
  {
    heading: "Current Surface",
    id: "current-surface",
    matches: (command) =>
      (command.scope === "dialog" ||
        (command.scope === "page" &&
          (command.kind === "navigate" || command.kind === "back"))) &&
      command.group !== "create",
  },
  {
    heading: "Current Page",
    id: "current-page",
    matches: (command) =>
      command.scope === "page" &&
      command.group !== "selection" &&
      command.group !== "create" &&
      command.kind !== "navigate" &&
      command.kind !== "back",
  },
  {
    heading: "Go To",
    id: "navigate",
    matches: (command) =>
      command.scope === "global" && command.group === "navigate",
  },
  {
    heading: "Utility",
    id: "utility",
    matches: (command) =>
      command.group === "create" ||
      command.group === "other" ||
      (command.scope === "global" && command.group !== "navigate"),
  },
]

const EMPTY_COMMANDS: AppCommand[] = []
const log = logger("CommandPalette")

export function CommandPalette({
  commands = EMPTY_COMMANDS,
  featureFlagStatus,
  improvementLoopNavEnabled,
}: {
  commands?: AppCommand[]
  featureFlagStatus: Partial<Record<NavigationFeatureFlag, boolean>>
  improvementLoopNavEnabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [openSource, setOpenSource] =
    React.useState<CommandPaletteOpenSource | null>(null)
  const [searchValue, setSearchValue] = React.useState("")
  const { commands: allCommands, globalNavigationShortcuts } =
    useAvailableCommands({
      enableEntityCommands: open,
      featureFlagStatus,
      improvementLoopNavEnabled,
    })

  useRegisterCommands(commands, commands.length > 0)

  React.useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail =
        event instanceof CustomEvent
          ? (event.detail as Record<string, unknown> | null)
          : null
      const nextSource =
        detail && typeof detail.source === "string"
          ? (detail.source as CommandPaletteOpenSource)
          : "keyboard-shortcut"

      setOpenSource(nextSource)
      setOpen(true)
    }

    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, handleOpen)

    return () => {
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, handleOpen)
    }
  }, [])

  React.useEffect(() => {
    if (!open) {
      setSearchValue("")
      setOpenSource(null)
    }
  }, [open])

  usePageShortcuts(globalNavigationShortcuts)

  const visibleCommands = React.useMemo(() => {
    if (searchValue.trim()) {
      return allCommands.filter((command) => !command.isBranch)
    }

    return allCommands.filter(
      (command) => !command.searchOnly && !command.isBranch,
    )
  }, [allCommands, searchValue])

  const commandsBySection = React.useMemo(() => {
    return COMMAND_SECTIONS.map((section) => ({
      ...section,
      commands: visibleCommands.filter((command) => section.matches(command)),
    })).filter((section) => section.commands.length > 0)
  }, [visibleCommands])

  useEscapeShortcut({
    description: "Close command menu",
    enabled: open,
    handler: () => {
      setOpen(false)
      setOpenSource(null)
    },
    priority: ESCAPE_SHORTCUT_PRIORITY.COMMAND_PALETTE,
  })

  const title =
    openSource === "keyboard-help" ? "Keyboard Shortcuts" : "Command Menu"

  const description =
    openSource === "keyboard-help"
      ? "Browse active shortcuts and navigation."
      : "Search destinations and active shortcuts."

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        size="lg"
        className="max-h-[min(80vh,720px)] overflow-hidden p-0"
      >
        <DialogHeader className="gap-1 px-5 pt-5">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Divider />
        <DialogBody className="px-5 py-4">
          <Command className="flex h-full flex-col gap-4 bg-transparent">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-coval-text-muted" />
              <CommandInput
                aria-label="Command palette search"
                className={cn(
                  inputVariants(),
                  "h-10 border-coval-border bg-surface-1 pl-9 pr-3 shadow-none",
                )}
                onValueChange={setSearchValue}
                placeholder="Jump to a page or run a shortcut..."
                value={searchValue}
              />
            </div>
            <CommandList className="max-h-[420px] flex-1 overflow-y-auto rounded-lg border border-coval-border bg-surface-1 p-2 scrollbar-thin scrollbar-thumb-[color:var(--coval-border)] scrollbar-track-transparent">
              <CommandEmpty>No results found.</CommandEmpty>
              {commandsBySection.map((section, sectionIndex) => (
                <React.Fragment key={section.id}>
                  {sectionIndex > 0 ? (
                    <CommandSeparator className="my-2">
                      <Divider />
                    </CommandSeparator>
                  ) : null}
                  <CommandGroup
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-coval-text-muted"
                    heading={section.heading}
                  >
                    {section.commands.map((command) => (
                      <CommandItem
                        className={cn(
                          "cursor-pointer rounded-md px-2 py-2.5 transition-colors",
                          "aria-selected:bg-surface-2",
                        )}
                        key={command.id}
                        onSelect={() => {
                          void Promise.resolve(command.action()).catch(
                            (error: unknown) => {
                              log.error(
                                "Failed to run command palette action",
                                error instanceof Error ? error : undefined,
                              )
                            },
                          )
                          setOpen(false)
                          setSearchValue("")
                        }}
                        value={[
                          command.label,
                          command.description,
                          command.key,
                          ...(command.keywords ?? []),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate text-sm font-medium text-coval-text">
                              {command.label}
                            </span>
                            {command.description ? (
                              <span className="truncate text-xs text-coval-text-muted">
                                {command.description}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2">
                            {command.key ? (
                              <kbd className="rounded-md border border-coval-border bg-surface-default px-2.5 py-1 text-[11px] font-mono font-medium text-coval-text-muted shadow-sm">
                                {command.key}
                              </kbd>
                            ) : null}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </React.Fragment>
              ))}
            </CommandList>
            <div className="flex items-center justify-end gap-2 text-xs text-coval-text-muted">
              <span className="rounded border border-coval-border bg-surface-1 px-1.5 py-0.5 font-mono">
                ↑↓
              </span>
              <span>navigate</span>
              <span className="rounded border border-coval-border bg-surface-1 px-1.5 py-0.5 font-mono">
                esc
              </span>
              <span>close</span>
            </div>
          </Command>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
